import { useCallback, useMemo, useState } from 'react'
import { Check, Mail, UserPlus, X } from 'lucide-react'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { PageHeaderStack } from '@/components/navigation/page-header-stack'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getPageHeader } from '@/constants'
import {
  useAccessRequests,
  useBulkInviteAccessRequests,
  useApproveAccessRequest,
  useInviteAccessRequest,
  useRejectAccessRequest,
  type AccessRequest,
  type AccessRequestStatus,
} from '@/hooks/use-access-requests'
import { getAccessRequestConfig } from '@/constants/signup'
import { getPluginSlots } from '@/plugin/slots'
import { useListFilterBar } from '@/hooks/use-list-filter-bar'
import {
  ADMIN_WAITLIST_FILTER_DEFINITIONS,
  type AdminWaitlistFilterState,
} from '@/lib/list-filters/admin-waitlist'

function statusBadge(status: AccessRequestStatus) {
  switch (status) {
    case 'pending':
      return <Badge variant="warningSoft">Pending</Badge>
    case 'session_sent':
      return <Badge variant="secondarySoft">Session sent</Badge>
    case 'invited':
      return <Badge variant="secondarySoft">Invited</Badge>
    case 'converted':
    case 'approved':
      return <Badge variant="successSoft">{status === 'converted' ? 'Converted' : 'Approved'}</Badge>
    case 'rejected':
      return <Badge variant="destructiveSoft">Rejected</Badge>
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

function formatDate(value?: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function AdminWaitlistPage() {
  const accessRequestConfig = getAccessRequestConfig()
  const adminPlugin = getPluginSlots().accessRequestAdmin
  const pageTitle = accessRequestConfig.adminLabel ?? 'Waitlist'
  const pageDescription =
    adminPlugin?.description ??
    accessRequestConfig.adminDescription ??
    'Review access requests and progressively invite people in'

  const [statusFilter, setStatusFilter] = useState<AdminWaitlistFilterState['status']>('pending')
  const [search, setSearch] = useState('')
  const [rowError, setRowError] = useState('')

  const { data, isLoading } = useAccessRequests({
    status: statusFilter || undefined,
    search: search || undefined,
    limit: 200,
  })
  const inviteMutation = useInviteAccessRequest()
  const approveMutation = useApproveAccessRequest()
  const rejectMutation = useRejectAccessRequest()
  const bulkInviteMutation = useBulkInviteAccessRequests()

  const items = useMemo(() => data?.items ?? [], [data?.items])
  const counts = useMemo(() => data?.counts ?? {}, [data?.counts])
  const pendingIds = useMemo(
    () =>
      items
        .filter((item) => item.status === 'pending' && item.source !== 'registration')
        .map((item) => item.id),
    [items],
  )
  const filterCtx = useMemo(() => ({ counts }), [counts])
  const busy =
    inviteMutation.isPending ||
    approveMutation.isPending ||
    rejectMutation.isPending ||
    bulkInviteMutation.isPending

  const handleInvite = (entry: AccessRequest) => {
    setRowError('')
    inviteMutation.mutate(entry.id, {
      onError: (error) => setRowError(error.message),
    })
  }

  const handleApprove = (entry: AccessRequest) => {
    setRowError('')
    approveMutation.mutate(entry.id, {
      onError: (error) => setRowError(error.message),
    })
  }

  const handleReject = (entry: AccessRequest) => {
    setRowError('')
    rejectMutation.mutate(entry.id, {
      onError: (error) => setRowError(error.message),
    })
  }

  const handleInviteAllPending = () => {
    setRowError('')
    bulkInviteMutation.mutate(pendingIds, {
      onSuccess: (result) => {
        const errorMessages = Object.values(result.errors ?? {})
        if (errorMessages.length > 0) {
          setRowError(`Some invites failed: ${errorMessages.join('; ')}`)
        }
      },
      onError: (error) => setRowError(error.message),
    })
  }

  const waitlistFilters: AdminWaitlistFilterState = useMemo(
    () => ({ search, status: statusFilter }),
    [search, statusFilter],
  )

  const onFiltersChange = useCallback((next: AdminWaitlistFilterState) => {
    setStatusFilter(next.status)
  }, [])

  const clearFilters = useCallback(() => {
    setSearch('')
    setStatusFilter('')
  }, [])

  const { filterBar } = useListFilterBar({
    listId: 'adminWaitlist',
    definitions: ADMIN_WAITLIST_FILTER_DEFINITIONS,
    filters: waitlistFilters,
    onFiltersChange,
    ctx: filterCtx,
    onClearAll: clearFilters,
    search: {
      searchId: 'admin-waitlist-search',
      searchPlaceholder: 'Search by email or name…',
      searchValue: search,
      onSearchChange: setSearch,
    },
  })

  return (
    <PageContainer>
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          {...getPageHeader('/platform/waitlist', pageTitle, pageDescription)}
          icon={<UserPlus className="h-5 w-5" />}
          actions={
            !adminPlugin?.hideBulkInvite && pendingIds.length > 0 ? (
              <Button variant="outline" size="sm" onClick={handleInviteAllPending} disabled={busy}>
                <Mail className="h-4 w-4" aria-hidden />
                Invite all pending ({pendingIds.length})
              </Button>
            ) : undefined
          }
        />

        {filterBar}
      </PageHeaderStack>

      {rowError ? (
        <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive" role="alert">
          {rowError}
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading access requests…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {statusFilter ? `No ${statusFilter} access requests.` : 'No access requests yet.'}
        </p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Reviewed</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="font-medium">{entry.email}</TableCell>
                  <TableCell>{entry.name || '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{entry.role || '—'}</TableCell>
                  <TableCell className="max-w-[180px] truncate text-muted-foreground" title={entry.company ?? undefined}>
                    {entry.company || '—'}
                  </TableCell>
                  <TableCell className="max-w-[240px] truncate text-muted-foreground" title={entry.note ?? undefined}>
                    {entry.note || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {entry.source === 'waitlist_form' ? 'Waitlist' : 'Registration'}
                  </TableCell>
                  <TableCell>{statusBadge(entry.status)}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(entry.created_at)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {entry.reviewed_by_email
                      ? `${formatDate(entry.reviewed_at)} · ${entry.reviewed_by_email}`
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    {adminPlugin ? (
                      <adminPlugin.RowActions
                        entry={entry}
                        busy={busy}
                        reject={() => handleReject(entry)}
                        setError={setRowError}
                      />
                    ) : entry.status === 'pending' ? (
                      <div className="inline-flex items-center gap-2">
                        {entry.source === 'registration' ? (
                          <Button size="sm" onClick={() => handleApprove(entry)} disabled={busy}>
                            <Check className="h-4 w-4" aria-hidden />
                            Approve
                          </Button>
                        ) : (
                          <Button size="sm" onClick={() => handleInvite(entry)} disabled={busy}>
                            <Mail className="h-4 w-4" aria-hidden />
                            Invite
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleReject(entry)}
                          disabled={busy}
                          aria-label={`Reject ${entry.email}`}
                        >
                          <X className="h-4 w-4" aria-hidden />
                        </Button>
                      </div>
                    ) : entry.status === 'invited' ? (
                      <Button size="sm" variant="outline" onClick={() => handleInvite(entry)} disabled={busy}>
                        <Mail className="h-4 w-4" aria-hidden />
                        Resend invite
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </PageContainer>
  )
}
